import type { ColumnDef } from '@tanstack/react-table';

import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { TruncatedTooltipValue } from '@/components/layout/TruncatedTooltipValue';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { ModalSize } from '@/features/configuration-management/location/table/Column';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { formatDate } from '@/lib/dateFormatter';
import { formatAmount } from '@/lib/format';
import type { customerPaymentLog } from '@/services/customerPaymentLogs/customerPaymentLogs.types';
import { ReactNode } from 'react';
import { ConfirmForm } from '../components/ConfirmForm';

const statusClasses: Record<customerPaymentLog['status'], string> = {
  PENDING: 'text-warning',
  APPROVED: 'text-success',
  REJECTED: 'text-destructive-text',
};
type onOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;
export function getColumns(
  onOpen: onOpenProps,
  t: ReturnType<typeof useDynamicLocale>['t'],
): ColumnDef<customerPaymentLog>[] {
  const statusLabels: Record<customerPaymentLog['status'], string> = {
    PENDING: t('common.page.table.pending'),
    APPROVED: t('common.page.table.approved'),
    REJECTED: t('paymentManagement.customerPaymentLogs.table.rejected'),
  };
  return [
    {
      id: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title="S.N" />,
      cell: ({ row, table }) => (
        <div className="font-medium text-xs text-foreground-label">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'requestID',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.requestId')} />,
      cell: ({ row }) => (
        <div className="font-medium text-xs text-foreground-label">{row.original.withdrawalRequestId}</div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'userInfo',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.userInfo')} />,
      cell: ({ row }) => {
        const userInfo = row.original.userInfo;
        return (
          <TruncatedTooltipValue
            content={userInfo}
            className="max-w-[12rem] text-xs text-foreground-label"
            tooltipClassName="max-w-[240px] break-words"
          />
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: 'emailAddress',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.emailAddress')} />
      ),
      cell: ({ row }) => (
        <a href={`mailto:${row.original.emailAddress}`} className="text-xs text-link underline break-all max-w-[200px]">
          {row.original.emailAddress}
        </a>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'phoneNumber',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.phoneNumber')} />
      ),
      cell: ({ row }) => <div className="text-xs text-foreground-label">{row.original.phoneNumber}</div>,
      size: 170,
      enableHiding: false,
    },
    {
      accessorKey: 'requestDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.requestDate')} />
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-xs text-foreground-label">
          {row.original.requestDate ? formatDate(Number(row.original.requestDate), 'YYYY/MM/DD') : '-'}
        </div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'requestedAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.requestedAmount')} />
      ),
      cell: ({ row }) => {
        const requestedAmount = row.original.requestedAmount ?? 0;

        return (
          <TruncatedTooltipValue
            content={formatAmount(requestedAmount)}
            className="block max-w-[8rem] whitespace-nowrap text-end text-xs text-foreground-label"
            tooltipClassName="max-w-[240px] "
          />
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: 'approvalDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.approvalDate')} />
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-xs text-foreground-label">
          {row.original.approvalDate ? formatDate(Number(row.original.approvalDate), 'YYYY/MM/DD') : '-'}
        </div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'remarks',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.remarks')} />,
      cell: ({ row }) => {
        const remarks = row.original.remarks?.trim();

        if (!remarks) {
          return <div className="text-xs text-muted-foreground">-</div>;
        }

        return (
          <TruncatedTooltipValue
            content={remarks}
            className="max-w-[12rem] text-xs text-foreground-label"
            tooltipClassName="max-w-[240px] break-words"
          />
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ row }) => {
        const status = row.original.status;

        return (
          <span
            className={`inline-flex items-center justify-center rounded-sm px-2.5 py-1 text-[11px] font-semibold capitalize ${statusClasses[status]}`}
          >
            {statusLabels[row.original.status]}
          </span>
        );
      },
      size: 110,
      enableHiding: false,
    },
    {
      id: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('common.page.tableColumn.action')}
          className="whitespace-nowrap flex items-center w-full"
        />
      ),
      cell: ({ row }) => {
        if (row.original.status === 'APPROVED' || row.original.status === 'REJECTED') {
          return <div className="text-xs text-foreground-label"></div>;
        }

        return (
          <Can rule={{ allOf: [PERMS.paymentManagement.customerPaymentLogs.manage] }}>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => {
                  onOpen({
                    modalTitle: 'common.page.modal.confirmation.title',
                    title: 'Confirmation',
                    cancelText: t('common.page.modal.cancel'),
                    okText: t('common.page.modal.yes'),
                    size: 'lg',
                    component: <ConfirmForm withdrawalId={row.original.withdrawalId} mode="reject" />,
                  });
                }}
                className="border rounded-[2px] border-destructive px-3 py-2 text-destructive-text text-[11px] font-bold inline-flex leading-none whitespace-nowrap cursor-pointer"
              >
                <Translation id="common.page.button.reject" />
              </button>
              <button
                type="button"
                onClick={() => {
                  onOpen({
                    modalTitle: 'common.page.modal.confirmation.title',
                    title: 'Confirmation',
                    cancelText: t('common.page.modal.cancel'),
                    okText: t('common.page.modal.yes'),
                    size: 'lg',
                    component: <ConfirmForm withdrawalId={row.original.withdrawalId} mode="approve" />,
                  });
                }}
                className="border rounded-[2px] bg-success px-3 py-2 text-on-color font-bold text-[11px] inline-flex leading-none whitespace-nowrap cursor-pointer"
              >
                <Translation id="common.page.button.approve" />
              </button>
            </div>
          </Can>
        );
      },
      size: 150,
      enableSorting: false,
    },
  ];
}
